import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
    Wine, Clock, CheckCircle, AlertTriangle,
    RefreshCcw, Timer, Bell, Coffee, Check,
    AlertCircle, Package, X, Play, Maximize2, Minimize2,
    Volume2, VolumeX, GlassWater, AlertOctagon, IceCream, Home
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import MobilePageHeader from '../components/MobilePageHeader';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const api = () => {
    const token = localStorage.getItem('token');
    return {
        get: async (url) => {
            const res = await fetch(`${API_BASE}/api${url}`, {
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return { data: await res.json() };
        },
        put: async (url, data) => {
            const res = await fetch(`${API_BASE}/api${url}`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return { data: await res.json() };
        },
        post: async (url, data) => {
            const res = await fetch(`${API_BASE}/api${url}`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return { data: await res.json() };
        }
    };
};

// ========================================
// TICKET DE COMMANDE BAR
// ========================================

const BarTicket = ({ 
    order, 
    onCommencer, 
    onPret, 
    onProbleme, 
    isFullscreen = false,
    showCommencer = false,
    showPret = false,
    isReady = false,
    isProblem = false,
    canAct = true
}) => {
    const getElapsedMinutes = () => {
        const startTime = order.sent_to_bar_at || order.sent_to_kitchen_at || order.created_at;
        if (!startTime) return 0;
        const start = new Date(startTime);
        const now = new Date();
        return Math.floor((now - start) / 60000);
    };

    const elapsedMinutes = getElapsedMinutes();
    
    const getTimeColor = () => {
        if (elapsedMinutes < 3) return 'text-green-400';
        if (elapsedMinutes < 6) return 'text-orange-400';
        return 'text-red-400';
    };

    const getBorderColor = () => {
        if (isProblem) return 'border-purple-500 bg-purple-50';
        if (isReady) return 'border-green-500 bg-green-50';
        if (elapsedMinutes >= 6) return 'border-red-500 bg-red-50 animate-pulse';
        if (elapsedMinutes >= 3) return 'border-orange-400 bg-orange-50';
        return 'border-indigo-300 bg-white';
    };

    const getHeaderBg = () => {
        if (isProblem) return 'bg-purple-600';
        if (isReady) return 'bg-green-600';
        if (elapsedMinutes >= 6) return 'bg-red-600';
        if (elapsedMinutes >= 3) return 'bg-orange-500';
        return 'bg-indigo-700';
    };

    const formatArrivalTime = () => {
        const time = order.sent_to_bar_at || order.sent_to_kitchen_at || order.created_at;
        if (!time) return '-';
        return new Date(time).toLocaleTimeString('fr-FR', {
            hour: '2-digit',
            minute: '2-digit',
            timeZone: 'Africa/Libreville'
        });
    };

    return (
        <div 
            className={cn(
                "rounded-xl border-2 overflow-hidden shadow-lg transition-all duration-300",
                getBorderColor()
            )}
            data-testid={`bar-ticket-${order.order_id}`}
        >
            {/* Header compact - Numéro + Table + Timer */}
            <div className={cn("px-3 py-2 text-white", getHeaderBg())}>
                <div className="flex items-center justify-between">
                    {/* Numéro et Table */}
                    <div className="flex items-center gap-2">
                        <span className={cn("font-black", isFullscreen ? "text-2xl" : "text-lg")}>
                            #{order.order_number?.split('-').pop() || order.order_id?.slice(-4)}
                        </span>
                        <Badge variant="secondary" className="bg-white/25 text-white text-xs px-2 py-0.5">
                            {order.table_number ? `Table ${order.table_number}` : 'Comptoir'}
                        </Badge>
                        {(order.is_takeaway || order.order_type === 'takeaway') && (
                            <Badge className="bg-yellow-500 text-black font-black text-xs px-3 py-1 animate-pulse">
                                📦 À EMPORTER
                            </Badge>
                        )}
                    </div>
                    
                    {/* Timer */}
                    <div className={cn(
                        "flex items-center gap-1 px-2 py-1 rounded-lg font-mono font-bold",
                        "bg-black/30",
                        getTimeColor()
                    )}>
                        <Timer className="w-3.5 h-3.5" />
                        <span className={isFullscreen ? "text-lg" : "text-sm"}>
                            {elapsedMinutes}m
                        </span>
                    </div>
                </div>
                
                {/* Info secondaire */}
                <div className="flex items-center gap-2 mt-1 text-white/70 text-xs">
                    <Clock className="w-3 h-3" />
                    <span>{formatArrivalTime()}</span>
                    {order.waiter_name && (
                        <>
                            <span>•</span>
                            <span>{order.waiter_name}</span>
                        </>
                    )}
                </div>
            </div>

            {/* Items - Liste compacte */}
            <div className="px-3 py-2 bg-white">
                <div className="space-y-1.5">
                    {order.items?.map((item, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                            <span className={cn(
                                "inline-flex items-center justify-center min-w-[24px] h-6 rounded bg-indigo-100 text-indigo-700 font-bold",
                                isFullscreen ? "text-sm" : "text-xs"
                            )}>
                                {item.quantity}x
                            </span>
                            <div className="flex-1 min-w-0">
                                <div className={cn(
                                    "font-medium text-gray-800",
                                    isFullscreen ? "text-base" : "text-sm"
                                )}>
                                    {item.name}
                                </div>
                                {/* Formula parent mention */}
                                {item.is_formula_item && item.formula_name && (
                                    <div className="text-xs text-purple-600 font-medium flex items-center gap-1">
                                        <Wine className="w-3 h-3" />
                                        {item.formula_name}
                                    </div>
                                )}
                                {/* Supplement badge */}
                                {item.is_supplement && (
                                    <div className="inline-flex items-center text-[10px] text-orange-600 bg-orange-100 px-1.5 py-0.5 rounded mt-0.5">
                                        SUPPLÉMENT
                                    </div>
                                )}
                                {item.supplements?.length > 0 && (
                                    <div className="text-xs text-gray-500 truncate">
                                        + {item.supplements.map(s => s.name || s).join(', ')}
                                    </div>
                                )}
                                {item.notes && (
                                    <div className="text-xs text-orange-600 italic flex items-center gap-1">
                                        <AlertCircle className="w-3 h-3 flex-shrink-0" />
                                        <span className="truncate">{item.notes}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {order.notes && (
                    <div className="mt-2 p-2 bg-yellow-50 rounded-lg border border-yellow-200 text-xs">
                        <strong>Note:</strong> {order.notes}
                    </div>
                )}
            </div>

            {/* Actions - Boutons compacts */}
            {canAct ? (
                (showCommencer || showPret) && (
                    <div className="px-3 py-2 bg-gray-50 border-t flex gap-2">
                        {showCommencer && (
                            <Button 
                                onClick={() => onCommencer(order)}
                                className="flex-1 bg-blue-600 hover:bg-blue-700 h-9"
                                size="sm"
                            >
                                <Play className="w-4 h-4 mr-1" />
                                Commencer
                            </Button>
                        )}
                        {showPret && (
                            <Button 
                                onClick={() => onPret(order)}
                                className="flex-1 bg-green-600 hover:bg-green-700 h-9"
                                size="sm"
                            >
                                <Check className="w-4 h-4 mr-1" />
                                PRÊT
                            </Button>
                        )}
                        <Button 
                            onClick={() => onProbleme(order)}
                            variant="outline"
                            className="border-purple-400 text-purple-600 hover:bg-purple-50 h-9 px-2"
                            size="sm"
                        >
                            <AlertOctagon className="w-4 h-4" />
                        </Button>
                    </div>
                )
            ) : (
                <div className="px-3 py-2 border-t bg-gray-100 text-center text-gray-500 text-xs">
                    <AlertCircle className="w-3 h-3 inline mr-1" />
                    Mode lecture seule
                </div>
            )}
        </div>
    );
};

// ========================================
// ÉCRAN PRINCIPAL BAR
// ========================================

const BarDashboardPage = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    
    // Note: les permissions sont vérifiées plus bas après tous les hooks
    
    const [tickets, setTickets] = useState([]);
    const [stats, setStats] = useState({ en_attente: 0, en_preparation: 0, prets: 0, problemes: 0, temps_moyen_minutes: 0 });
    const [loading, setLoading] = useState(true);
    const [isPaused, setIsPaused] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [soundEnabled, setSoundEnabled] = useState(true);
    const [currentTime, setCurrentTime] = useState(new Date());
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [problemNote, setProblemNote] = useState('');
    const [showProblemDialog, setShowProblemDialog] = useState(false);
    const [readyOrdersToRemove, setReadyOrdersToRemove] = useState(new Set());
    
    // Messages from salle
    const [barMessages, setBarMessages] = useState([]);
    const [showMessagesPanel, setShowMessagesPanel] = useState(true);
    
    // Settings for auto-dismiss - 5 seconds as requested (notification sent to POS)
    const AUTO_DISMISS_DELAY = 5; // seconds
    
    const audioRef = useRef(null);
    const prevTicketCount = useRef(0);
    const dismissTimersRef = useRef(new Map());
    const readyTimestampsRef = useRef(new Map()); // Track when orders became ready

    // Mark ticket as notified in backend (persisted)
    const markAsNotified = async (orderId) => {
        try {
            await api().post(`/bar/tickets/${orderId}/notify`);
            console.log(`[Bar] Commande ${orderId} marquée comme notifiée`);
        } catch (error) {
            console.error('Erreur lors de la notification:', error);
        }
    };

    // Charger les données
    const fetchData = useCallback(async () => {
        if (isPaused) return;
        
        try {
            const [ticketsRes, statsRes, messagesRes] = await Promise.all([
                api().get('/bar/tickets?include_all=true'),
                api().get('/bar/stats'),
                api().get('/bar/messages?limit=10').catch(() => ({ data: [] }))
            ]);
            
            setTickets(ticketsRes.data || []);
            setStats(statsRes.data || { en_attente: 0, en_preparation: 0, prets: 0, problemes: 0, temps_moyen_minutes: 0 });
            setBarMessages(messagesRes.data || []);
            
            // Notification sonore pour nouvelles commandes
            const currentCount = (ticketsRes.data || []).length;
            if (soundEnabled && currentCount > prevTicketCount.current && prevTicketCount.current > 0) {
                playNotificationSound();
            }
            prevTicketCount.current = currentCount;
            
        } catch (error) {
            console.error('Erreur chargement bar:', error);
        } finally {
            setLoading(false);
        }
    }, [isPaused, soundEnabled]);

    // Son notification - avec gestion des erreurs de permission
    const playNotificationSound = () => {
        if (audioRef.current) {
            audioRef.current.currentTime = 0;
            audioRef.current.play().catch(() => {
                // Silently ignore - browser blocks autoplay without user interaction
            });
        }
    };

    // Rafraîchissement automatique
    useEffect(() => {
        fetchData();
        const interval = setInterval(fetchData, 10000); // Refresh every 10 seconds
        return () => {
            clearInterval(interval);
            // Clear all dismiss timers
            dismissTimersRef.current.forEach(timer => clearTimeout(timer));
        };
    }, [fetchData]);

    // Auto-dismiss ready orders after 15 seconds AND persist to backend
    useEffect(() => {
        const readyOrders = tickets.filter(t => 
            (t.bar_status === 'pret') && 
            !readyOrdersToRemove.has(t.order_id)
        );
        
        readyOrders.forEach(order => {
            // Skip if already has a timer
            if (dismissTimersRef.current.has(order.order_id)) return;
            
            // Track when this order became ready (if not already tracked)
            if (!readyTimestampsRef.current.has(order.order_id)) {
                readyTimestampsRef.current.set(order.order_id, Date.now());
            }
            
            const timer = setTimeout(async () => {
                // Mark as notified in backend (persistent)
                await markAsNotified(order.order_id);
                
                // Also remove from local view
                setReadyOrdersToRemove(prev => new Set([...prev, order.order_id]));
                dismissTimersRef.current.delete(order.order_id);
                readyTimestampsRef.current.delete(order.order_id);
            }, AUTO_DISMISS_DELAY * 1000);
            
            dismissTimersRef.current.set(order.order_id, timer);
        });
        
        // Cleanup timers for orders that are no longer ready
        dismissTimersRef.current.forEach((timer, orderId) => {
            const stillReady = tickets.some(t => 
                t.order_id === orderId && t.bar_status === 'pret'
            );
            if (!stillReady) {
                clearTimeout(timer);
                dismissTimersRef.current.delete(orderId);
                readyTimestampsRef.current.delete(orderId);
            }
        });
    }, [tickets, readyOrdersToRemove]);

    // Mise à jour de l'horloge
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    // Filtrer les tickets par statut (excluding auto-dismissed ones)
    const pendingTickets = tickets.filter(t => t.bar_status === 'nouveau' || !t.bar_status);
    const preparingTickets = tickets.filter(t => t.bar_status === 'en_preparation');
    const readyTickets = tickets.filter(t => t.bar_status === 'pret' && !readyOrdersToRemove.has(t.order_id));
    const problemTickets = tickets.filter(t => t.bar_status === 'probleme');

    // Actions
    const handleCommencer = async (order) => {
        try {
            await api().put(`/bar/tickets/${order.order_id}/status`, { status: 'en_preparation' });
            toast.success(`Commande #${order.order_number?.split('-').pop()} en préparation`);
            fetchData();
        } catch (error) {
            toast.error('Erreur lors du démarrage');
        }
    };

    const handlePret = async (order) => {
        try {
            await api().put(`/bar/tickets/${order.order_id}/status`, { status: 'pret' });
            toast.success(`Commande #${order.order_number?.split('-').pop()} prête!`);
            
            // Envoyer notification
            await api().post('/bar/notifications/bar-ready', {
                order_id: order.order_id,
                table_number: order.table_number,
                order_number: order.order_number
            });
            
            fetchData();
        } catch (error) {
            toast.error('Erreur');
        }
    };

    const handleProbleme = (order) => {
        setSelectedOrder(order);
        setProblemNote('');
        setShowProblemDialog(true);
    };

    const confirmProbleme = async () => {
        if (!selectedOrder) return;
        
        try {
            await api().put(`/bar/tickets/${selectedOrder.order_id}/status`, { 
                status: 'probleme',
                problem_note: problemNote 
            });
            toast.warning(`Problème signalé pour Table ${selectedOrder.table_number}`);
            setShowProblemDialog(false);
            fetchData();
        } catch (error) {
            toast.error('Erreur');
        }
    };

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen();
            setIsFullscreen(true);
        } else {
            document.exitFullscreen();
            setIsFullscreen(false);
        }
    };

    // Fonctions pour les messages
    const markMessageAsRead = async (messageId) => {
        try {
            await api().put(`/bar/messages/${messageId}/read`);
            setBarMessages(prev => prev.map(m => 
                m.id === messageId ? { ...m, read: true } : m
            ));
        } catch (error) {
            console.error('Error marking message as read:', error);
        }
    };

    const deleteMessage = async (messageId) => {
        try {
            await api().delete(`/bar/messages/${messageId}`);
            setBarMessages(prev => prev.filter(m => m.id !== messageId));
        } catch (error) {
            console.error('Error deleting message:', error);
        }
    };

    // Vérifier les permissions
    // SERVEUR a accès en lecture seule (consultation)
    const allowedRoles = ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT', 'BARMAN', 'SERVEUR', 'SERVEUSE'];
    const canActRoles = ['ADMIN', 'BARMAN']; // Seuls ces rôles peuvent valider/modifier
    const canAct = user && canActRoles.includes(user.role);
    const isReadOnlyMode = user && ['DIRECTEUR', 'DIRECTEUR_ADJOINT', 'SERVEUR', 'SERVEUSE'].includes(user.role);
    
    if (!user || !allowedRoles.includes(user.role)) {
        return (
            <div className="h-screen bg-gray-900 flex items-center justify-center">
                <div className="text-center text-white">
                    <Wine className="w-16 h-16 mx-auto mb-4 text-indigo-400" />
                    <h1 className="text-2xl font-bold mb-2">Accès Restreint</h1>
                    <p className="text-gray-400">Écran réservé au personnel du bar</p>
                    <Button 
                        variant="outline" 
                        className="mt-4"
                        onClick={() => navigate('/dashboard')}
                    >
                        Retour
                    </Button>
                </div>
            </div>
        );
    }

    // Format de l'heure Gabon
    const formatGabonTime = () => {
        return currentTime.toLocaleTimeString('fr-FR', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            timeZone: 'Africa/Libreville'
        });
    };

    return (
        <div className="h-screen bg-gradient-to-br from-indigo-950 via-purple-950 to-slate-900 overflow-hidden p-2 sm:p-0" data-testid="bar-dashboard">
            {/* Mobile Page Header */}
            <MobilePageHeader 
                title="Bar"
                subtitle={stats?.pending > 0 ? `${stats?.pending} en attente` : undefined}
                icon={Wine}
                backPath="/dashboard"
                darkMode={true}
                className="bg-gradient-to-r from-indigo-950/95 via-purple-950/95 to-slate-900/95 border-white/10"
            />
            
            {/* Audio pour notifications */}
            <audio ref={audioRef} preload="auto">
                <source src="/notification.mp3" type="audio/mpeg" />
            </audio>
            
            {/* Header - Responsive - Hidden on mobile */}
            <div className="hidden md:flex h-12 sm:h-16 bg-black/30 backdrop-blur-sm border-b border-white/10 items-center justify-between px-2 sm:px-6">
                <div className="flex items-center gap-2 sm:gap-4">
                    <div className="flex items-center gap-1 sm:gap-2">
                        <Wine className="w-5 h-5 sm:w-8 sm:h-8 text-indigo-400" />
                        <span className="text-lg sm:text-2xl font-bold text-white tracking-wider">BAR</span>
                    </div>
                    
                    {!isPaused && (
                        <Badge className="bg-green-500/20 text-green-400 animate-pulse text-xs">
                            <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-400 rounded-full mr-1 sm:mr-2"></span>
                            LIVE
                        </Badge>
                    )}
                    
                    {/* Badge lecture seule pour serveurs */}
                    {isReadOnlyMode && (
                        <Badge className="bg-amber-500/20 text-amber-400 text-xs">
                            LECTURE SEULE
                        </Badge>
                    )}
                </div>

                <div className="flex items-center gap-1 sm:gap-3">
                    {/* Bouton Retour à l'accueil */}
                    <Button
                        variant="ghost"
                        size="icon"
                        className="text-white h-8 w-8 sm:h-10 sm:w-10"
                        onClick={() => navigate('/dashboard')}
                        data-testid="home-btn"
                    >
                        <Home className="w-4 h-4 sm:w-5 sm:h-5" />
                    </Button>
                    
                    {/* Horloge */}
                    <div className="text-base sm:text-2xl font-mono text-white bg-black/30 px-2 sm:px-4 py-1 rounded">
                        {formatGabonTime()}
                    </div>

                    {/* Boutons de contrôle */}
                    <Button
                        variant="ghost"
                        size="icon"
                        className="text-white h-8 w-8 sm:h-10 sm:w-10"
                        onClick={() => setSoundEnabled(!soundEnabled)}
                    >
                        {soundEnabled ? <Volume2 className="w-4 h-4 sm:w-5 sm:h-5" /> : <VolumeX className="w-4 h-4 sm:w-5 sm:h-5" />}
                    </Button>
                    
                    <Button
                        variant="ghost"
                        size="icon"
                        className="text-white h-8 w-8 sm:h-10 sm:w-10 hidden sm:flex"
                        onClick={toggleFullscreen}
                    >
                        {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
                    </Button>
                    
                    <Button
                        variant={isPaused ? "default" : "ghost"}
                        className={cn(
                            "text-xs sm:text-sm h-8 sm:h-10 px-2 sm:px-4",
                            isPaused ? "bg-yellow-500" : "text-white"
                        )}
                        onClick={() => setIsPaused(!isPaused)}
                    >
                        {isPaused ? "REPRENDRE" : "PAUSE"}
                    </Button>
                    
                    <Button
                        variant="ghost"
                        size="icon"
                        className="text-white h-8 w-8 sm:h-10 sm:w-10"
                        onClick={fetchData}
                    >
                        <RefreshCcw className={cn("w-4 h-4 sm:w-5 sm:h-5", loading && "animate-spin")} />
                    </Button>
                </div>
            </div>

            {/* Stats bar - Responsive */}
            <div className="h-auto sm:h-20 bg-black/20 flex flex-wrap items-center justify-center gap-2 sm:gap-8 p-2 sm:px-6">
                <div className="flex items-center gap-2 sm:gap-3 bg-blue-500/20 rounded-lg sm:rounded-xl px-3 sm:px-6 py-2 sm:py-3">
                    <GlassWater className="w-5 h-5 sm:w-8 sm:h-8 text-blue-400" />
                    <div>
                        <div className="text-xl sm:text-3xl font-bold text-white">{stats.en_attente || pendingTickets.length}</div>
                        <div className="text-[10px] sm:text-xs text-blue-300">Attente</div>
                    </div>
                </div>
                
                <div className="flex items-center gap-2 sm:gap-3 bg-orange-500/20 rounded-lg sm:rounded-xl px-3 sm:px-6 py-2 sm:py-3">
                    <Coffee className="w-5 h-5 sm:w-8 sm:h-8 text-orange-400" />
                    <div>
                        <div className="text-xl sm:text-3xl font-bold text-white">{stats.en_preparation || preparingTickets.length}</div>
                        <div className="text-[10px] sm:text-xs text-orange-300">Prépa</div>
                    </div>
                </div>
                
                <div className="flex items-center gap-2 sm:gap-3 bg-green-500/20 rounded-lg sm:rounded-xl px-3 sm:px-6 py-2 sm:py-3">
                    <CheckCircle className="w-5 h-5 sm:w-8 sm:h-8 text-green-400" />
                    <div>
                        <div className="text-xl sm:text-3xl font-bold text-white">{readyTickets.length}</div>
                        <div className="text-[10px] sm:text-xs text-green-300">Prêtes</div>
                    </div>
                </div>
                
                <div className="flex items-center gap-2 sm:gap-3 bg-purple-500/20 rounded-lg sm:rounded-xl px-3 sm:px-6 py-2 sm:py-3">
                    <AlertOctagon className="w-5 h-5 sm:w-8 sm:h-8 text-purple-400" />
                    <div>
                        <div className="text-xl sm:text-3xl font-bold text-white">{stats.problemes || problemTickets.length}</div>
                        <div className="text-[10px] sm:text-xs text-purple-300">Problèmes</div>
                    </div>
                </div>
                
                <div className="hidden sm:flex items-center gap-3 bg-gray-500/20 rounded-xl px-6 py-3">
                    <Timer className="w-8 h-8 text-gray-400" />
                    <div>
                        <div className="text-3xl font-bold text-white">{stats.temps_moyen_minutes?.toFixed(1) || 0}</div>
                        <div className="text-xs text-gray-300">Temps moy.</div>
                    </div>
                </div>
            </div>

            {/* Messages de la salle */}
            {barMessages.length > 0 && (
                <div className={cn(
                    "mx-2 sm:mx-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg sm:rounded-xl shadow-lg mb-2",
                    isFullscreen ? "p-4" : "p-3"
                )}>
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                            <Bell className={cn("animate-bounce", isFullscreen ? "w-6 h-6" : "w-5 h-5")} />
                            <span className={cn("font-bold", isFullscreen ? "text-xl" : "text-sm sm:text-base")}>
                                MESSAGES DE LA SALLE ({barMessages.filter(m => !m.read).length} non lu{barMessages.filter(m => !m.read).length > 1 ? 's' : ''})
                            </span>
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowMessagesPanel(!showMessagesPanel)}
                            className="text-white hover:bg-white/20 h-7 px-2"
                        >
                            {showMessagesPanel ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                        </Button>
                    </div>
                    
                    {showMessagesPanel && (
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                            {barMessages.map(msg => (
                                <div 
                                    key={msg.id}
                                    className={cn(
                                        "bg-white/10 backdrop-blur rounded-lg p-2 sm:p-3 flex items-start gap-3",
                                        !msg.read && "bg-white/20 border-l-4 border-yellow-400"
                                    )}
                                >
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <Badge className="bg-amber-500 text-white text-xs px-2">
                                                {msg.table_number ? `Table ${msg.table_number}` : 'Sans table'}
                                            </Badge>
                                            <span className="text-xs text-white/70 truncate">
                                                {msg.sender_name}
                                            </span>
                                            <span className="text-xs text-white/50">
                                                {new Date(msg.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                            </span>
                                        </div>
                                        <p className={cn(
                                            "text-white font-medium",
                                            isFullscreen ? "text-base" : "text-sm"
                                        )}>
                                            {msg.message}
                                        </p>
                                    </div>
                                    <div className="flex gap-1 flex-shrink-0">
                                        {!msg.read && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => markMessageAsRead(msg.id)}
                                                className="text-white hover:bg-white/20 h-7 w-7 p-0"
                                                title="Marquer comme lu"
                                            >
                                                <Check className="w-4 h-4" />
                                            </Button>
                                        )}
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => deleteMessage(msg.id)}
                                            className="text-white hover:bg-red-500/50 h-7 w-7 p-0"
                                            title="Supprimer"
                                        >
                                            <X className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* VERSION MOBILE : Onglets */}
            <div className="sm:hidden p-2">
                <Tabs defaultValue="attente" className="w-full">
                    <TabsList className="grid grid-cols-4 w-full mb-2">
                        <TabsTrigger value="attente" className="text-xs px-1 data-[state=active]:bg-blue-500 data-[state=active]:text-white">
                            Attente ({pendingTickets.length})
                        </TabsTrigger>
                        <TabsTrigger value="preparation" className="text-xs px-1 data-[state=active]:bg-orange-500 data-[state=active]:text-white">
                            Prépa ({preparingTickets.length})
                        </TabsTrigger>
                        <TabsTrigger value="pretes" className="text-xs px-1 data-[state=active]:bg-green-500 data-[state=active]:text-white">
                            Prêtes ({readyTickets.length})
                        </TabsTrigger>
                        <TabsTrigger value="problemes" className="text-xs px-1 data-[state=active]:bg-purple-500 data-[state=active]:text-white">
                            Pb ({problemTickets.length})
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="attente" className="mt-0">
                        <div className="bg-blue-500/10 rounded-lg border border-blue-500/30 h-[calc(100vh-220px)] overflow-hidden">
                            <ScrollArea className="h-full p-2">
                                <div className="space-y-2">
                                    {pendingTickets.length === 0 ? (
                                        <div className="text-center text-gray-400 py-8">
                                            <GlassWater className="w-10 h-10 mx-auto mb-2 opacity-30" />
                                            <p className="text-sm">Aucune commande</p>
                                        </div>
                                    ) : (
                                        pendingTickets.map(ticket => (
                                            <BarTicket key={ticket.order_id} order={ticket}
                                                onCommencer={handleCommencer} onPret={handlePret} onProbleme={handleProbleme}
                                                isFullscreen={false} showCommencer={true} canAct={canAct} />
                                        ))
                                    )}
                                </div>
                            </ScrollArea>
                        </div>
                    </TabsContent>

                    <TabsContent value="preparation" className="mt-0">
                        <div className="bg-orange-500/10 rounded-lg border border-orange-500/30 h-[calc(100vh-220px)] overflow-hidden">
                            <ScrollArea className="h-full p-2">
                                <div className="space-y-2">
                                    {preparingTickets.length === 0 ? (
                                        <div className="text-center text-gray-400 py-8">
                                            <Coffee className="w-10 h-10 mx-auto mb-2 opacity-30" />
                                            <p className="text-sm">Aucune préparation</p>
                                        </div>
                                    ) : (
                                        preparingTickets.map(ticket => (
                                            <BarTicket key={ticket.order_id} order={ticket}
                                                onCommencer={handleCommencer} onPret={handlePret} onProbleme={handleProbleme}
                                                isFullscreen={false} showPret={true} canAct={canAct} />
                                        ))
                                    )}
                                </div>
                            </ScrollArea>
                        </div>
                    </TabsContent>

                    <TabsContent value="pretes" className="mt-0">
                        <div className="bg-green-500/10 rounded-lg border border-green-500/30 h-[calc(100vh-220px)] overflow-hidden">
                            <ScrollArea className="h-full p-2">
                                <div className="space-y-2">
                                    {readyTickets.length === 0 ? (
                                        <div className="text-center text-gray-400 py-8">
                                            <CheckCircle className="w-10 h-10 mx-auto mb-2 opacity-30" />
                                            <p className="text-sm">Aucune commande prête</p>
                                        </div>
                                    ) : (
                                        readyTickets.map(ticket => (
                                            <BarTicket key={ticket.order_id} order={ticket}
                                                onCommencer={handleCommencer} onPret={handlePret} onProbleme={handleProbleme}
                                                isFullscreen={false} isReady={true} canAct={canAct} />
                                        ))
                                    )}
                                </div>
                            </ScrollArea>
                        </div>
                    </TabsContent>

                    <TabsContent value="problemes" className="mt-0">
                        <div className="bg-purple-500/10 rounded-lg border border-purple-500/30 h-[calc(100vh-220px)] overflow-hidden">
                            <ScrollArea className="h-full p-2">
                                <div className="space-y-2">
                                    {problemTickets.length === 0 ? (
                                        <div className="text-center text-gray-400 py-8">
                                            <AlertOctagon className="w-10 h-10 mx-auto mb-2 opacity-30" />
                                            <p className="text-sm">Aucun problème</p>
                                        </div>
                                    ) : (
                                        problemTickets.map(ticket => (
                                            <BarTicket key={ticket.order_id} order={ticket}
                                                onCommencer={handleCommencer} onPret={handlePret} onProbleme={handleProbleme}
                                                isFullscreen={false} isProblem={true} showCommencer={true} canAct={canAct} />
                                        ))
                                    )}
                                </div>
                            </ScrollArea>
                        </div>
                    </TabsContent>
                </Tabs>
            </div>

            {/* VERSION DESKTOP : Colonnes - Optimisées */}
            <div className="hidden sm:grid h-[calc(100vh-9rem)] grid-cols-4 gap-3 p-3">
                {/* EN ATTENTE */}
                <div className="bg-blue-500/10 rounded-xl border border-blue-500/30 overflow-hidden flex flex-col">
                    <div className="bg-blue-600 text-white px-3 py-2 flex items-center justify-between flex-shrink-0">
                        <div className="flex items-center gap-2">
                            <GlassWater className="w-5 h-5" />
                            <span className="font-bold text-sm">EN ATTENTE</span>
                        </div>
                        <Badge variant="secondary" className="bg-white/20 text-white text-base font-bold">
                            {pendingTickets.length}
                        </Badge>
                    </div>
                    <ScrollArea className="flex-1 p-2">
                        <div className="space-y-2">
                            {pendingTickets.length === 0 ? (
                                <div className="text-center text-gray-400 py-8">
                                    <GlassWater className="w-10 h-10 mx-auto mb-2 opacity-30" />
                                    <p className="text-sm">Aucune commande</p>
                                </div>
                            ) : (
                                pendingTickets.map(ticket => (
                                    <BarTicket
                                        key={ticket.order_id}
                                        order={ticket}
                                        onCommencer={handleCommencer}
                                        onPret={handlePret}
                                        onProbleme={handleProbleme}
                                        isFullscreen={isFullscreen}
                                        showCommencer={true}
                                        canAct={canAct}
                                    />
                                ))
                            )}
                        </div>
                    </ScrollArea>
                </div>

                {/* EN PRÉPARATION */}
                <div className="bg-orange-500/10 rounded-xl border border-orange-500/30 overflow-hidden flex flex-col">
                    <div className="bg-orange-500 text-white px-3 py-2 flex items-center justify-between flex-shrink-0">
                        <div className="flex items-center gap-2">
                            <Coffee className="w-5 h-5" />
                            <span className="font-bold text-sm">EN PRÉPARATION</span>
                        </div>
                        <Badge variant="secondary" className="bg-white/20 text-white text-base font-bold">
                            {preparingTickets.length}
                        </Badge>
                    </div>
                    <ScrollArea className="flex-1 p-2">
                        <div className="space-y-2">
                            {preparingTickets.length === 0 ? (
                                <div className="text-center text-gray-400 py-8">
                                    <Coffee className="w-10 h-10 mx-auto mb-2 opacity-30" />
                                    <p className="text-sm">Aucune préparation</p>
                                </div>
                            ) : (
                                preparingTickets.map(ticket => (
                                    <BarTicket
                                        key={ticket.order_id}
                                        order={ticket}
                                        onCommencer={handleCommencer}
                                        onPret={handlePret}
                                        onProbleme={handleProbleme}
                                        isFullscreen={isFullscreen}
                                        showPret={true}
                                        canAct={canAct}
                                    />
                                ))
                            )}
                        </div>
                    </ScrollArea>
                </div>

                {/* PRÊTES */}
                <div className="bg-green-500/10 rounded-xl border border-green-500/30 overflow-hidden flex flex-col">
                    <div className="bg-green-600 text-white px-3 py-2 flex items-center justify-between flex-shrink-0">
                        <div className="flex items-center gap-2">
                            <CheckCircle className="w-5 h-5" />
                            <span className="font-bold text-sm">PRÊTES</span>
                        </div>
                        <Badge variant="secondary" className="bg-white/20 text-white text-base font-bold">
                            {readyTickets.length}
                        </Badge>
                    </div>
                    <ScrollArea className="flex-1 p-2">
                        <div className="space-y-2">
                            {readyTickets.length === 0 ? (
                                <div className="text-center text-gray-400 py-8">
                                    <CheckCircle className="w-10 h-10 mx-auto mb-2 opacity-30" />
                                    <p className="text-sm">Aucune commande prête</p>
                                </div>
                            ) : (
                                readyTickets.map(ticket => (
                                    <BarTicket
                                        key={ticket.order_id}
                                        order={ticket}
                                        onCommencer={handleCommencer}
                                        onPret={handlePret}
                                        onProbleme={handleProbleme}
                                        isFullscreen={isFullscreen}
                                        isReady={true}
                                        canAct={canAct}
                                    />
                                ))
                            )}
                        </div>
                    </ScrollArea>
                </div>

                {/* PROBLÈMES */}
                <div className="bg-purple-500/10 rounded-xl border border-purple-500/30 overflow-hidden flex flex-col">
                    <div className="bg-purple-600 text-white px-3 py-2 flex items-center justify-between flex-shrink-0">
                        <div className="flex items-center gap-2">
                            <AlertOctagon className="w-5 h-5" />
                            <span className="font-bold text-sm">PROBLÈMES</span>
                        </div>
                        <Badge variant="secondary" className="bg-white/20 text-white text-base font-bold">
                            {problemTickets.length}
                        </Badge>
                    </div>
                    <ScrollArea className="flex-1 p-2">
                        <div className="space-y-2">
                            {problemTickets.length === 0 ? (
                                <div className="text-center text-gray-400 py-8">
                                    <AlertOctagon className="w-10 h-10 mx-auto mb-2 opacity-30" />
                                    <p className="text-sm">Aucun problème</p>
                                </div>
                            ) : (
                                problemTickets.map(ticket => (
                                    <BarTicket
                                        key={ticket.order_id}
                                        order={ticket}
                                        onCommencer={handleCommencer}
                                        onPret={handlePret}
                                        onProbleme={handleProbleme}
                                        isFullscreen={isFullscreen}
                                        isProblem={true}
                                        showCommencer={true}
                                        canAct={canAct}
                                    />
                                ))
                            )}
                        </div>
                    </ScrollArea>
                </div>
            </div>

            {/* Dialog Problème */}
            <Dialog open={showProblemDialog} onOpenChange={setShowProblemDialog}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <AlertOctagon className="w-5 h-5 text-purple-600" />
                            Signaler un problème
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                            Commande #{selectedOrder?.order_number?.split('-').pop()} - Table {selectedOrder?.table_number}
                        </p>
                        <Textarea
                            placeholder="Décrivez le problème (rupture stock, équipement, etc.)"
                            value={problemNote}
                            onChange={(e) => setProblemNote(e.target.value)}
                            rows={3}
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowProblemDialog(false)}>
                            Annuler
                        </Button>
                        <Button 
                            className="bg-purple-600 hover:bg-purple-700"
                            onClick={confirmProbleme}
                        >
                            Signaler
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default BarDashboardPage;
